import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  CartItem,
  CheckoutBuyerIdentity,
  createShopifyCart,
  addLineToShopifyCart,
  updateShopifyCartLine,
  removeLineFromShopifyCart,
  fetchShopifyCart,
  updateCartBuyerIdentity,
  updateCartAttributes,
  type CartAttribute,
} from '@/lib/shopify';
import { toast } from 'sonner';
import { gtmAddToCart, gtmRemoveFromCart, gadsAddToCart, gtmException } from '@/lib/gtmDataLayer';
import { cartItemToFeedOfferId } from '@/lib/feedOfferId';

import { supabase } from '@/integrations/supabase/client';
import { wrapWithCheckoutProxy } from '@/lib/checkoutProxy';
import { getStoredAdClick, getStoredGoogleClickIdentifier, buildAttributionAttributes, logChainEvent } from '@/lib/gclidCapture';
import { getGaClientId, getGaSessionId, waitForGaClientId } from '@/lib/gaClientId';
import { logAttributionEvent } from '@/lib/attributionLogger';
import { devError, devWarn } from "@/lib/devLog";
import { logAddToCartFailure, type AddToCartFailureReason } from "@/lib/addToCartFailureLog";

function logCartFailure(reason: AddToCartFailureReason, item: CartItem, message?: string) {
  logAddToCartFailure({
    reason,
    message: message ?? null,
    productId: item.product?.node?.id ?? null,
    productHandle: item.productHandle ?? item.product?.node?.handle ?? null,
    productTitle: item.product?.node?.title ?? null,
    variantId: item.variantId ?? null,
    variantTitle: item.variantTitle ?? null,
    quantity: item.quantity ?? null,
    source: "cart-store",
  });
}

const mergeCartAttributes = (base: CartAttribute[], extras: CartAttribute[]): CartAttribute[] => {
  const merged = [...base];
  for (const extra of extras) {
    const idx = merged.findIndex((attr) => attr.key === extra.key);
    if (idx >= 0) merged[idx] = extra;
    else merged.push(extra);
  }
  return merged;
};

const buildVerifiedCheckoutAttributes = async (extraAttributes: CartAttribute[]): Promise<CartAttribute[]> => {
  const attrs = buildAttributionAttributes();
  const gaClientId = getGaClientId() || await waitForGaClientId(350) || `fallback-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  if (gaClientId) attrs.push({ key: '_ga_client_id', value: gaClientId });
  const gaSessionId = getGaSessionId();
  if (gaSessionId) attrs.push({ key: '_ga_session_id', value: gaSessionId });
  attrs.push({ key: '_attribution_verified_at', value: new Date().toISOString() });
  return mergeCartAttributes(attrs, extraAttributes);
};

const buildReserveAttributionAttributes = (reason: string, extraAttributes: CartAttribute[] = []): CartAttribute[] => {
  const click = getStoredGoogleClickIdentifier();
  const attrs = mergeCartAttributes(buildAttributionAttributes(), extraAttributes);
  const gaClientId = getGaClientId() || `fallback-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const gaSessionId = getGaSessionId();

  return mergeCartAttributes(attrs, [
    { key: '_ga_client_id', value: gaClientId },
    ...(gaSessionId ? [{ key: '_ga_session_id', value: gaSessionId }] : []),
    { key: '_utm_source', value: click ? 'google' : 'zooi' },
    { key: '_utm_medium', value: click ? 'cpc' : 'checkout_fallback' },
    { key: '_utm_campaign', value: 'checkout_attribution_reserve' },
    { key: '_attribution_mode', value: 'reserve' },
    { key: '_attribution_fallback_reason', value: reason },
    { key: '_attribution_fallback_at', value: new Date().toISOString() },
    { key: '_fallback_source', value: click ? 'google' : 'site' },
    ...(click ? [
      { key: `_${click.type}`, value: click.value },
      { key: '_google_click_id', value: click.value },
      { key: '_google_click_id_type', value: click.type },
      { key: '_fallback_medium', value: 'cpc' },
      { key: '_fallback_click_id_type', value: click.type },
      { key: '_fallback_click_id', value: click.value },
    ] : []),
  ]);
};

const appendReserveAttributionToCheckoutUrl = (checkoutUrl: string): string => {
  const click = getStoredGoogleClickIdentifier();
  try {
    const url = new URL(checkoutUrl);
    url.searchParams.set('utm_source', url.searchParams.get('utm_source') || (click ? 'google' : 'zooi'));
    url.searchParams.set('utm_medium', url.searchParams.get('utm_medium') || (click ? 'cpc' : 'checkout_fallback'));
    url.searchParams.set('utm_campaign', url.searchParams.get('utm_campaign') || 'checkout_attribution_reserve');
    if (click) url.searchParams.set(click.type, click.value);
    return url.toString();
  } catch {
    return checkoutUrl;
  }
};

// wrapWithCheckoutProxy moved to '@/lib/checkoutProxy' for testability.

/** Per-cart secret so only the creating session may write the abandoned-cart row. */
const getAbandonedCartToken = (cartId: string): string => {
  const key = `zooi_abandoned_cart_token:${cartId}`;
  try {
    const existing = localStorage.getItem(key);
    if (existing && existing.length >= 16) return existing;
    const token = crypto.randomUUID() + crypto.randomUUID().slice(0, 8);
    localStorage.setItem(key, token);
    return token;
  } catch {
    return crypto.randomUUID() + crypto.randomUUID().slice(0, 8);
  }
};

/** Fire-and-forget: save abandoned cart to DB when Shopify cart is created */
const saveAbandonedCartSilent = async (cartId: string, items: CartItem[], totalPrice: number) => {
  const cartItems = items.map(item => ({
    variantId: item.variantId,
    title: item.product.node.title,
    quantity: item.quantity,
    price: item.price.amount,
    image: item.product.node.images?.edges?.[0]?.node?.url || '',
    handle: item.product.node.handle,
  }));

  try {
    const { error } = await supabase.rpc('save_abandoned_cart', {
      p_cart_id: cartId,
      p_session_token: getAbandonedCartToken(cartId),
      p_cart_items: cartItems as never,
      p_total_price: totalPrice,
      p_currency: 'SEK',
      p_email: null,
      p_email_source: null,
      p_reached_checkout: false,
    });
    if (error) devError('Failed to save abandoned cart:', error);
  } catch (e) {
    devError('Failed to save abandoned cart:', e);
  }
};


const compactCartItem = (item: CartItem): CartItem => ({
  ...item,
  // Surface handle at top level so feed-aligned item_id survives the product
  // stripping below (which removes variants/options to keep localStorage small).
  productHandle: item.productHandle ?? item.product.node.handle,
  product: {
    node: {
      id: item.product.node.id,
      title: item.product.node.title,
      description: '',
      handle: item.product.node.handle,
      vendor: item.product.node.vendor,
      priceRange: item.product.node.priceRange,
      ...(item.product.node.compareAtPriceRange
        ? { compareAtPriceRange: item.product.node.compareAtPriceRange }
        : {}),
      images: {
        edges: item.product.node.images?.edges?.slice(0, 1) ?? [],
      },
      variants: {
        edges: [],
      },
      options: [],
      inventoryQuantity: item.product.node.inventoryQuantity ?? null,
    },
  },
});


interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;

  // Actions
  addItem: (item: CartItem) => Promise<boolean>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  createCheckout: (buyerIdentity: CheckoutBuyerIdentity, extraAttributes?: CartAttribute[]) => Promise<string | null>;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,

      addItem: async (item) => {
        const { items, cartId, clearCart } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);
        const max = item.maxQuantity ?? existingItem?.maxQuantity;

        set({ isLoading: true });
        try {
          if (!cartId) {
            const result = await createShopifyCart(item);
            if (result) {
              const newItem = compactCartItem({ ...item, lineId: result.lineId });
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                items: [newItem],
              });
              gtmAddToCart({
                item_id: cartItemToFeedOfferId({ ...item, productHandle: newItem.productHandle }),
                item_name: item.product.node.title,
                price: parseFloat(item.price.amount),
                quantity: item.quantity,
                item_variant: item.variantTitle || undefined,
                item_brand: item.product.node.vendor || undefined,
                currency: item.price.currencyCode || "SEK",
              });
              gadsAddToCart({ id: item.variantId, price: parseFloat(item.price.amount) });

              // Auto-save abandoned cart to DB
              saveAbandonedCartSilent(result.cartId, [newItem], parseFloat(item.price.amount) * item.quantity);
              // Attach attribution attributes immediately so gclid/wbraid/gbraid
              // survives even if the user never clicks "Till kassa" inside our UI.
              // Idempotent: helper returns [] when no ad-click data exists.
              try {
                const attrs = buildAttributionAttributes();
                const gaClientId = getGaClientId();
                if (gaClientId) attrs.push({ key: '_ga_client_id', value: gaClientId });
                const gaSessionId = getGaSessionId();
                if (gaSessionId) attrs.push({ key: '_ga_session_id', value: gaSessionId });
                if (attrs.length) {
                  void updateCartAttributes(result.cartId, attrs).then((ok) => {
                    // Trace event so we can see exactly which attributes left the
                    // browser. This is the cart_attach link in the chain — if the
                    // uploader later sees `_chain_id` but no `_gclid`, we know it
                    // was dropped between Shopify cart and order.note_attributes.
                    logChainEvent(ok ? 'cart_attach' : 'cart_attach_failed', {
                      payload: {
                        cart_id_suffix: result.cartId.slice(-12),
                        attribute_keys: attrs.map((a) => a.key),
                        has_gclid: attrs.some((a) => a.key === '_gclid'),
                        has_chain_id: attrs.some((a) => a.key === '_chain_id'),
                      },
                    });
                  }).catch(() => {});
                }
              } catch (e) {
                devWarn('[cart] attach attribution on create failed:', e);
              }

              return true;
            }

            toast.error("Kunde inte lägga till i varukorgen", { position: 'top-center' });
            logCartFailure('shopify_create_cart_failed', item, 'createShopifyCart returned null');
            return false;
          }

          if (existingItem) {
            const newQty = existingItem.quantity + item.quantity;
            if (max && newQty > max) {
              toast.error(`Bara ${max} st i lager`, { position: 'top-center' });
              logCartFailure('insufficient_stock', item, `max=${max}, requestedQty=${newQty}`);
              if (existingItem.quantity >= max) return false;

              if (existingItem.lineId) {
                const result = await updateShopifyCartLine(cartId, existingItem.lineId, max);
                if (result.success) {
                  set({ items: get().items.map(i => i.variantId === item.variantId ? { ...i, quantity: max } : i) });
                } else if (result.cartNotFound) {
                  clearCart();
                }
              }
              return false;
            }

            if (!existingItem.lineId) {
              if (import.meta.env.DEV) {
                devError('Cannot update item without lineId');
              }
              toast.error('Något gick fel – försök igen');
              logCartFailure('missing_line_id', item, 'existing cart item has no lineId');
              return false;
            }

            const result = await updateShopifyCartLine(cartId, existingItem.lineId, newQty);
            if (result.success) {
              set({ items: get().items.map(i => i.variantId === item.variantId ? { ...i, quantity: newQty } : i) });
              gtmAddToCart({
                item_id: cartItemToFeedOfferId(item),
                item_name: item.product.node.title,
                price: parseFloat(item.price.amount),
                quantity: item.quantity,
                item_variant: item.variantTitle || undefined,
                item_brand: item.product.node.vendor || undefined,
                currency: item.price.currencyCode || "SEK",
              });
              gadsAddToCart({ id: item.variantId, price: parseFloat(item.price.amount) });

              return true;
            }

            if (result.cartNotFound) {
              clearCart();
              logCartFailure('cart_not_found', item, 'updateShopifyCartLine reported cart not found');
            } else {
              logCartFailure('shopify_update_line_failed', item);
            }
            return false;
          }

          let itemToAdd = item;
          if (max && item.quantity > max) {
            toast.error(`Bara ${max} st i lager`, { position: 'top-center' });
            itemToAdd = { ...item, quantity: max };
          }

          const result = await addLineToShopifyCart(cartId, itemToAdd);
          if (result.success) {
            set({ items: [...get().items, compactCartItem({ ...itemToAdd, lineId: result.lineId ?? null })] });
            gtmAddToCart({
              item_id: cartItemToFeedOfferId(itemToAdd),
              item_name: itemToAdd.product.node.title,
              price: parseFloat(itemToAdd.price.amount),
              quantity: itemToAdd.quantity,
              item_variant: itemToAdd.variantTitle || undefined,
              item_brand: itemToAdd.product.node.vendor || undefined,
              currency: itemToAdd.price.currencyCode || "SEK",
            });
            gadsAddToCart({ id: itemToAdd.variantId, price: parseFloat(itemToAdd.price.amount) });

            // Re-attach full attribution set on every line-add so _ga_client_id
            // (which loads async via gtag) gets captured even if cart was created
            // before GA was ready. cartAttributesUpdate replaces the full set.
            try {
              const attrs: CartAttribute[] = buildAttributionAttributes();
              const gaClientId = getGaClientId();
              if (gaClientId) attrs.push({ key: '_ga_client_id', value: gaClientId });
              const gaSessionId = getGaSessionId();
              if (gaSessionId) attrs.push({ key: '_ga_session_id', value: gaSessionId });
              if (attrs.length) void updateCartAttributes(cartId, attrs);
            } catch (e) {
              devWarn('[cart] re-attach attribution on line-add failed:', e);
            }
            return true;
          }

          if (result.cartNotFound) {
            clearCart();
            logCartFailure('cart_not_found', item, 'addLineToShopifyCart reported cart not found');
          } else {
            logCartFailure('shopify_add_line_failed', item);
          }
          return false;
        } catch (error) {
          devError('Failed to add item:', error);
          gtmException('cart_add_item_failed', false);
          toast.error("Kunde inte lägga till i varukorgen", { position: 'top-center' });
          logCartFailure('unexpected_error', item, error instanceof Error ? error.message : String(error));
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) {
          await get().removeItem(variantId);
          return;
        }

        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        if (item.maxQuantity && quantity > item.maxQuantity) {
          toast.error(`Bara ${item.maxQuantity} st i lager`, { position: 'top-center' });
          return;
        }

        set({ isLoading: true });
        try {
          const result = await updateShopifyCartLine(cartId, item.lineId, quantity);
          if (result.success) {
            set({ items: get().items.map(i => i.variantId === variantId ? { ...i, quantity } : i) });
          } else if (result.cartNotFound) {
            clearCart();
          } else {
            toast.error("Kunde inte uppdatera antal – försök igen", { position: 'top-center' });
          }
        } catch (error) {
          devError('Failed to update quantity:', error);
          gtmException('cart_update_quantity_failed', false);
          toast.error("Kunde inte uppdatera antal", { position: 'top-center' });
        } finally {
          set({ isLoading: false });
        }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item) return;

        // GTM tracking
        gtmRemoveFromCart({
          item_id: item.variantId,
          item_name: item.product.node.title,
          price: parseFloat(item.price.amount),
          quantity: item.quantity,
          currency: item.price.currencyCode || "SEK",
        });

        if (!item.lineId || !cartId) {
          // Fallback: just remove locally
          const newItems = items.filter(i => i.variantId !== variantId);
          newItems.length === 0 ? clearCart() : set({ items: newItems });
          return;
        }

        set({ isLoading: true });
        try {
          const result = await removeLineFromShopifyCart(cartId, item.lineId);
          if (result.success) {
            const newItems = get().items.filter(i => i.variantId !== variantId);
            newItems.length === 0 ? clearCart() : set({ items: newItems });
          } else if (result.cartNotFound) {
            clearCart();
          }
        } catch (error) {
          devError('Failed to remove item:', error);
          gtmException('cart_remove_item_failed', false);
        } finally {
          set({ isLoading: false });
        }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null }),

      syncCart: async () => {
        const { cartId, isSyncing, clearCart, items } = get();
        if (!cartId || isSyncing) return;

        set({ isSyncing: true });
        try {
          const result = await fetchShopifyCart(cartId);
          if (!result.exists) {
            clearCart();
          } else {
            // Reconcile local quantities with Shopify's actual cart state
            if (result.lines && result.lines.length > 0) {
              const updatedItems = items
                .map(item => {
                  const shopifyLine = result.lines!.find(l => l.variantId === item.variantId);
                  if (!shopifyLine) return null; // Item no longer in Shopify cart
                  return {
                    ...item,
                    quantity: shopifyLine.quantity,
                    lineId: shopifyLine.lineId,
                  };
                })
                .filter(Boolean) as CartItem[];

              if (updatedItems.length === 0) {
                clearCart();
              } else {
                set({
                  items: updatedItems,
                  checkoutUrl: result.checkoutUrl || get().checkoutUrl,
                });
              }
            } else if (result.checkoutUrl) {
              set({ checkoutUrl: result.checkoutUrl });
            }
          }
        } catch (error) {
          devError('Failed to sync cart:', error);
          gtmException('cart_sync_failed', false);
        } finally {
          set({ isSyncing: false });
        }
      },

      createCheckout: async (buyerIdentity: CheckoutBuyerIdentity, extraAttributes: CartAttribute[] = []) => {
        const { cartId, clearCart } = get();
        if (!cartId) return null;

        set({ isLoading: true });
        try {
          // ─── Verified attribution + extra attributes ───
          // PMax purchases must carry gclid/gbraid/wbraid before redirecting to Shopify;
          // otherwise GA4 can attribute revenue to `(not set)`. This is bounded below.
          const attributionPromise = buildVerifiedCheckoutAttributes(extraAttributes)
            .then((attrs) => updateCartAttributes(cartId, attrs))
            .catch((e) => {
              devWarn('Verified attribution flush failed:', e);
              return false;
            });

          // ─── Buyer identity update: the critical call ───
          // Run in parallel with attribution — this is what gives us the checkout URL.
          const buyerPromise = updateCartBuyerIdentity(cartId, buyerIdentity);

          // Wait for both, but only buyer identity is required for checkout.
          // Attribution has a 1.2s internal timeout so it won't hold us forever.
          let [attributionVerified, checkoutUrl] = await Promise.all([
            // Attribution: bounded wait (2.5s max), returns false on timeout
            Promise.race([attributionPromise, new Promise<boolean>(r => setTimeout(() => r(false), 2500))]),
            buyerPromise,
          ]);

          if (!attributionVerified) {
            const reserveAttrs = buildReserveAttributionAttributes('verification_failed_before_redirect', extraAttributes);
            attributionVerified = await Promise.race([
              updateCartAttributes(cartId, reserveAttrs),
              new Promise<boolean>(r => setTimeout(() => r(false), 700)),
            ]);
          }

          if (checkoutUrl) {
            const url = new URL(checkoutUrl);
            if (buyerIdentity.email) {
              url.searchParams.set('checkout[email]', buyerIdentity.email);
            }
            if (buyerIdentity.phone) {
              url.searchParams.set('checkout[shipping_address][phone]', buyerIdentity.phone);
            }
            const addr = buyerIdentity.deliveryAddressPreferences?.[0]?.deliveryAddress;
            if (addr) {
              if (addr.firstName) url.searchParams.set('checkout[shipping_address][first_name]', addr.firstName);
              if (addr.lastName) url.searchParams.set('checkout[shipping_address][last_name]', addr.lastName);
              if (addr.address1) url.searchParams.set('checkout[shipping_address][address1]', addr.address1);
              if (addr.city) url.searchParams.set('checkout[shipping_address][city]', addr.city);
              if (addr.zip) url.searchParams.set('checkout[shipping_address][zip]', addr.zip);
              if (addr.country) url.searchParams.set('checkout[shipping_address][country]', addr.country);
            }
            const finalUrl = attributionVerified ? url.toString() : appendReserveAttributionToCheckoutUrl(url.toString());
            const proxiedFinal = await wrapWithCheckoutProxy(finalUrl, cartId);
            set({ checkoutUrl: proxiedFinal });
            logAttributionEvent({
              event_name: 'attribution.checkout_linked',
              status: attributionVerified ? 'success' : 'failed',
              event_id: getStoredAdClick()?.sessionId,
              payload_summary: {
                cart_id_suffix: cartId.slice(-12),
                verified_before_redirect: attributionVerified,
                has_google_click_id: !!getStoredAdClick()?.gclid || !!getStoredAdClick()?.gbraid || !!getStoredAdClick()?.wbraid,
                has_ga_client_id: !!getGaClientId(),
              },
              ...(!attributionVerified ? { error_message: 'checkout attribution was not verified before redirect' } : {}),
            });
            return proxiedFinal;
          }

          // Fallback: if buyerIdentity update failed, try existing checkoutUrl
          // (from cart creation) — better to send user to Shopify without
          // pre-filled fields than to block the entire checkout.
          const existing = get().checkoutUrl;
          if (existing) {
            devWarn('Buyer identity update failed — using fallback checkout URL');
            void updateCartAttributes(cartId, buildReserveAttributionAttributes('buyer_identity_failed', extraAttributes));
            gtmException('checkout_buyer_identity_fallback', false);
            return await wrapWithCheckoutProxy(appendReserveAttributionToCheckoutUrl(existing), cartId);
          }

          return null;
        } catch (error) {
          devError('Failed to create checkout:', error);
          gtmException('checkout_creation_failed', true);
          // Last-resort fallback: if we have ANY checkout URL, use it
          const lastResort = get().checkoutUrl;
          if (lastResort) {
            devWarn('Checkout failed with error — using last-resort fallback URL');
            void updateCartAttributes(cartId, buildReserveAttributionAttributes('checkout_exception', extraAttributes));
            return await wrapWithCheckoutProxy(appendReserveAttributionToCheckoutUrl(lastResort), cartId);
          }
          return null;
        } finally {
          set({ isLoading: false });
        }
      },

      getTotalItems: () => get().items.reduce((sum, item) => sum + item.quantity, 0),
      getTotalPrice: () => get().items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0),
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items, cartId: state.cartId, checkoutUrl: state.checkoutUrl }),
    }
  )
);
